/**
 * Primitives that know no domain. Behaviour that is hard to get right comes
 * from Radix, unstyled; the look comes from tokens.css. Everything else is a
 * plain element with a class.
 */
import { T } from '@/i18n';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as RTooltip from '@radix-ui/react-tooltip';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { create } from 'zustand';
import { Icon } from './icons';

type Tone = 'ok' | 'warn' | 'dgr' | 'neu' | 'act';

export function Button({
  children,
  tone,
  small,
  ghost,
  className,
  ...rest
}: {
  children: ReactNode;
  /** pub: the publish recipe (green), for the action that makes drafts visible to the employees (#154) */
  tone?: 'pri' | 'dgr' | 'pub';
  small?: boolean;
  ghost?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  // a caller's className is added to the button classes, not put in their place: spreading it
  // through `rest` after className replaced `btn sm` and left the phone Filter disclosure native
  const cls = ['btn', tone, small ? 'sm' : '', ghost ? 'ghost' : '', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} {...rest}>
      {children}
    </button>
  );
}

/**
 * A real checkbox with role="switch": Space and a tap both toggle it, the whole label is the target.
 * Classes: .switch (label), .switch.off (disabled), input:checked+i (on), input:focus-visible+i (ring).
 */
export function Switch({
  checked,
  onChange,
  label,
  disabled,
  id,
  title,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  disabled?: boolean | undefined;
  id?: string | undefined;
  title?: string | undefined;
}) {
  return (
    <label className={`switch${disabled ? ' off' : ''}`} title={title}>
      <input
        type="checkbox"
        role="switch"
        aria-checked={checked}
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <i />
      <span>{label}</span>
    </label>
  );
}

export const Chip = ({
  tone,
  children,
  small,
  'data-testid': testId,
}: { tone: Tone; children: ReactNode; small?: boolean; 'data-testid'?: string }) => (
  // data-testid passes through: it was dropped, so a test id set on a chip found nothing
  <span className={`chip ${tone}${small ? ' sm' : ''}`} data-testid={testId}>
    {children}
  </span>
);

export const Tag = ({ children, kind }: { children: ReactNode; kind?: 'miss' | 'ovr' }) => (
  <span className={`tag${kind ? ` ${kind}` : ''}`}>{children}</span>
);

/**
 * One rule for where a panel's title goes (owner, 2026-09-21, plan 0025 "Containers"):
 * a card that holds a region of content (a table, list, chart, form, or its empty state) is a
 * section, its title and actions sit on the page above the card, whatever state it is in. A card
 * that IS one item (a KPI, a single next task) keeps its label inside: pass `item`.
 */
export function Panel({
  title,
  sub,
  actions,
  children,
  flush,
  item,
  id,
}: {
  /** an anchor a link can land on (`/innstillinger/lonn#tripletex`) */
  id?: string;
  title?: ReactNode;
  sub?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  item?: boolean;
}) {
  const head = title != null && (
    <div className={item ? 'ch' : 'sech'}>
      {item ? <b>{title}</b> : <h2>{title}</h2>}
      {sub != null && <span className="sub">{sub}</span>}
      <span className="sp" />
      {actions}
    </div>
  );
  if (item)
    return (
      <div className="card2">
        {head}
        <div className={`cb${flush ? ' flush' : ''}`}>{children}</div>
      </div>
    );
  return (
    <section className="sec" id={id}>
      {head}
      <div className="card2">
        <div className={`cb${flush ? ' flush' : ''}`}>{children}</div>
      </div>
    </section>
  );
}

export function Drawer({
  open,
  onClose,
  title,
  sub,
  children,
  footer,
  blockClose,
  initialFocus,
  wide,
  docked = false,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  sub?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Returns true to keep the drawer open: the owner asks the person first (TF-SWITCH-RECOVERY). */
  blockClose?: () => boolean;
  /** the element to focus on open instead of the dialog itself, when it exists (the editor opened to fill one field) */
  initialFocus?: (() => HTMLElement | null) | undefined;
  /** a form with two columns of fields: the employee editor */
  wide?: boolean;
  docked?: boolean;
}) {
  const opener = useRef<HTMLElement | null>(null);
  const [desktop, setDesktop] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(min-width: 1101px)').matches,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const media = matchMedia('(min-width: 1101px)');
    const update = () => setDesktop(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);
  if (docked && desktop) {
    if (!open) return null;
    return (
      <aside
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !e.defaultPrevented) {
            if (!blockClose?.()) onClose();
          }
        }}
        className="mpanel docked-drawer"
        aria-label={typeof title === 'string' ? title : undefined}
      >
        <div className="mph">
          <div>
            <h3>{title}</h3>
            {sub != null && <p>{sub}</p>}
          </div>
          <button
            type="button"
            className="mpx"
            aria-label={T('Lukk')}
            onClick={() => {
              if (!blockClose?.()) onClose();
            }}
          >
            <Icon name="close" />
          </button>
        </div>
        <div className="mpb">{children}</div>
        {footer != null && <div className="mpf">{footer}</div>}
      </aside>
    );
  }
  // every dismissal path (the X, Escape, the backdrop) arrives here
  const attempt = () => {
    if (blockClose?.()) return;
    onClose();
  };
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && attempt()}>
      <Dialog.Portal>
        <Dialog.Overlay className="scrim" />
        <Dialog.Content
          className={`drawer${wide ? ' wide' : ''}`}
          aria-describedby={undefined}
          onOpenAutoFocus={(e) => {
            opener.current = document.activeElement as HTMLElement | null;
            const el = initialFocus?.();
            if (!el) return;
            e.preventDefault();
            el.focus();
          }}
          onCloseAutoFocus={(e) => {
            if (opener.current?.isConnected && opener.current !== document.body) {
              e.preventDefault();
              opener.current.focus();
              return;
            }
            // Saving can replace the launching action or remove its filtered row.
            const target =
              document.querySelector<HTMLElement>('#main .mpanel h2') ??
              document.querySelector<HTMLElement>('#main h1');
            if (target) {
              e.preventDefault();
              target.focus();
            }
          }}
        >
          <div className="dh">
            <div>
              <Dialog.Title asChild>
                <h3>{title}</h3>
              </Dialog.Title>
              {sub != null && <p>{sub}</p>}
            </div>
            <Dialog.Close asChild>
              <button type="button" className="x" aria-label={T('Lukk')}>
                <Icon name="close" />
              </button>
            </Dialog.Close>
          </div>
          <div className="db">{children}</div>
          {footer != null && <div className="df">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Menu({
  trigger,
  items,
  tip,
  open,
  onOpenChange,
}: {
  trigger: ReactNode;
  items: { key: string; label: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean }[];
  /** Hover text for a trigger that is only a glyph. The tooltip wraps the menu trigger, which is
      the composition Radix supports: two asChild triggers around one button. */
  tip?: string;
  /** controlled, for a menu that also opens another way (the shift card's right-click) */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const t = <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>;
  return (
    <DropdownMenu.Root {...(open === undefined ? {} : { open })} {...(onOpenChange ? { onOpenChange } : {})}>
      {tip ? <Tip text={tip}>{t}</Tip> : t}
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="menu" align="end" sideOffset={6}>
          {items.map((it) => (
            <DropdownMenu.Item
              key={it.key}
              className={it.danger ? 'dgr' : ''}
              disabled={it.disabled ?? false}
              onSelect={it.onSelect}
            >
              {it.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export const TooltipProvider = RTooltip.Provider;
export function Tip({ text, children }: { text: ReactNode; children: ReactNode }) {
  return (
    <RTooltip.Root delayDuration={200}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content className="tip" sideOffset={6}>
          {text}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  );
}

/* toast: one at a time, optional undo */
interface ToastState {
  msg: string | null;
  undo: (() => void) | null;
  show: (msg: string, undo?: () => void) => void;
  hide: () => void;
}
let timer: ReturnType<typeof setTimeout> | null = null;
export const useToast = create<ToastState>((set) => ({
  msg: null,
  undo: null,
  show: (msg, undo) => {
    set({ msg, undo: undo ?? null });
    if (timer) clearTimeout(timer);
    // long enough to read (owner, 2026-09-27: a demo notice vanished half read): about 60 ms a
    // character on top of a base, at least as before, never more than 12 s
    timer = setTimeout(() => set({ msg: null, undo: null }), toastMs(msg, Boolean(undo)));
  },
  hide: () => set({ msg: null, undo: null }),
}));
/** How long a toast stays: 2.6 s for a short one (6 s with undo), longer with the text, 12 s at most. */
export const toastMs = (msg: string, undo = false) =>
  Math.min(12000, Math.max(undo ? 6000 : 2600, 1500 + msg.length * 60));
export const toast = (msg: string, undo?: () => void) => useToast.getState().show(msg, undo);
export function Toast() {
  const { msg, undo, hide } = useToast();
  if (!msg) return null;
  return (
    <output className="toast on">
      {msg}
      {undo && (
        <button
          type="button"
          onClick={() => {
            undo();
            hide();
          }}
        >
          {T('Angre')}
        </button>
      )}
    </output>
  );
}

/**
 * The three designed states of a panel with nothing to list (plan 0025, empty states):
 * `done` caught up (green check, what is true now), `setup` not set up (what is missing, an
 * action that creates it), `error` could not load (neutral, never red, with a retry). With no
 * tone it is the plain line it always was.
 */
export function Empty({
  children,
  tone,
  title,
  action,
}: {
  children?: ReactNode;
  tone?: 'done' | 'setup' | 'error';
  title?: ReactNode;
  action?: ReactNode;
}) {
  if (!tone && !title && !action) return <div className="empty">{children}</div>;
  const icon = tone === 'done' ? 'check' : tone === 'setup' ? 'plus' : tone === 'error' ? 'refresh' : null;
  return (
    <div className={`empty rich${tone ? ` ${tone}` : ''}`}>
      {icon && (
        <span className="eic" aria-hidden="true">
          <Icon name={icon} size={20} strokeWidth={2.2} />
        </span>
      )}
      {title && <b>{title}</b>}
      {children && <span>{children}</span>}
      {action}
    </div>
  );
}

/**
 * Loading, drawn as the rows it stands in for so the page does not jump when they arrive.
 * No spinner inside a card (plan 0025). Screen readers hear the words.
 */
export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <output className="skel" aria-live="polite" aria-busy="true">
      <span className="sr">{T('Henter…')}</span>
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: placeholders have no identity
        <span key={i} className="skrow" aria-hidden="true">
          <i className="skav" />
          <span>
            <i style={{ width: `${55 - i * 8}%` }} />
            <i style={{ width: '30%' }} />
          </span>
        </span>
      ))}
    </output>
  );
}

/** A load that failed: said plainly, confined to where it happened, with a retry. */
export function LoadError({ what, detail, onRetry }: { what: string; detail?: string; onRetry: () => void }) {
  return (
    <Empty
      tone="error"
      title={T('Kunne ikke hente {what}', { what })}
      action={
        <Button small onClick={onRetry}>
          {T('Prøv igjen')}
        </Button>
      }
    >
      {detail ? `${T('Resten av siden virker.')} (${detail})` : T('Resten av siden virker.')}
    </Empty>
  );
}
